export const FULLSCREEN_VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

/**
 * Vesper's whole exterior view in one deterministic pass. The Crown is a
 * depth-integrated analytical field rather than point geometry: three hot
 * filaments orbit through a noisy gaseous cross-section while the camera
 * choreography changes the field's apparent scale and placement.
 */
export const VESPER_FRAGMENT_SHADER = /* glsl */ `
  precision highp float;

  varying vec2 vUv;
  uniform float uTime;
  uniform float uProgress;
  uniform float uAspect;
  uniform float uSeed;
  uniform float uQuality;
  uniform vec2 uLook;
  uniform float uMotion;

  #define PI 3.141592653589793
  #define TAU 6.283185307179586
  #define MAX_CROWN_STEPS 42

  float saturate(float x) { return clamp(x, 0.0, 1.0); }
  float ss(float a, float b, float x) { return smoothstep(a, b, x); }

  mat2 rot(float a) {
    float c = cos(a);
    float s = sin(a);
    return mat2(c, -s, s, c);
  }

  float hash11(float p) {
    p = fract(p * 0.1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
  }

  float hash21(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  vec2 hash22(vec2 p) {
    float n = hash21(p);
    return vec2(n, hash21(p + n + 19.19));
  }

  float noise2(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash21(i), hash21(i + vec2(1.0, 0.0)), f.x),
      mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), f.x),
      f.y
    );
  }

  float fbm2(vec2 p) {
    float sum = 0.0;
    float amp = 0.52;
    mat2 turn = mat2(0.80, 0.60, -0.60, 0.80);
    for (int i = 0; i < 5; i++) {
      sum += noise2(p) * amp;
      p = turn * p * 2.07 + vec2(17.7, 31.1);
      amp *= 0.5;
    }
    return sum;
  }

  float wrappedDistance(float a, float b) {
    return abs(atan(sin(a - b), cos(a - b)));
  }

  vec3 starTemperature(float h) {
    vec3 blue = vec3(0.48, 0.74, 1.18);
    vec3 pearl = vec3(1.12, 1.08, 0.96);
    vec3 violet = vec3(0.68, 0.48, 1.12);
    vec3 c = mix(blue, pearl, ss(0.18, 0.72, h));
    return mix(c, violet, ss(0.88, 1.0, h) * 0.55);
  }

  float starLayer(vec2 p, float scale, float threshold, out float heat) {
    vec2 cell = p * scale;
    vec2 id = floor(cell);
    vec2 gv = fract(cell) - 0.5;
    vec2 offset = (hash22(id + uSeed * 13.7) - 0.5) * 0.72;
    float h = hash21(id + uSeed * 29.1);
    float power = ss(threshold, 1.0, h);
    float size = mix(0.035, 0.145, pow(h, 9.0));
    float d = length(gv - offset);
    float star = ss(size, 0.0, d) * power;
    float twinkle = 0.88 + 0.12 * sin(uTime * uMotion * (0.35 + h) + h * 71.0);
    heat = hash21(id + 91.7);
    return star * twinkle;
  }

  vec3 renderStars(vec2 p, float transit, float shore) {
    vec2 drift = vec2(uProgress * 0.055, -uProgress * 0.018) + uLook * 0.075;
    float h1;
    float h2;
    float h3;
    float a = starLayer(p + drift, 56.0, 0.966, h1);
    float b = starLayer(p * 1.11 - drift * 1.6, 126.0, 0.982, h2);
    float c = starLayer(p * 0.93 + drift * 2.1, 246.0, 0.992, h3);
    vec3 stars = starTemperature(h1) * a * 2.0;
    stars += starTemperature(h2) * b * 1.35;
    stars += starTemperature(h3) * c * 0.82;
    stars *= mix(1.0, 0.48, transit);
    stars *= mix(1.0, 1.16, shore);
    return stars;
  }

  vec3 renderDeepSky(vec2 p, float transit, float shore) {
    vec2 look = uLook * vec2(0.13, 0.09);
    vec2 q = p + look;
    float portrait = 1.0 - ss(0.78, 1.08, uAspect);

    float lowCloud = fbm2(q * 0.72 + vec2(uSeed * 0.17, 4.1));
    float highCloud = fbm2(rot(-0.44) * q * 1.55 + vec2(13.0, uSeed * 0.31));
    vec3 col = vec3(0.0028, 0.0038, 0.014);
    col += vec3(0.007, 0.010, 0.038) * (0.30 + lowCloud * 0.68);

    // A broad, irregular galactic band. Two dark sub-lanes keep it from
    // reading as a generic gradient and give every black interval an edge.
    float bandAngle = mix(-0.38, -0.62, portrait) + uProgress * 0.09;
    vec2 bandUv = rot(bandAngle) * (q + mix(vec2(0.10, 0.07), vec2(0.0, -0.04), portrait));
    float bandWarp = (fbm2(vec2(bandUv.x * 0.82, 2.7 + uSeed)) - 0.5) * 0.25;
    float signedBand = bandUv.y + bandWarp;
    float band = exp(-abs(signedBand) * 2.45);
    float bandDetail = fbm2(vec2(bandUv.x * 1.35, bandUv.y * 5.2) + uSeed * 0.23);
    float knots = pow(fbm2(vec2(bandUv.x * 2.8, bandUv.y * 8.5) - uSeed * 0.41), 2.2);

    vec3 cyanGas = vec3(0.026, 0.17, 0.23);
    vec3 ionGas = vec3(0.115, 0.035, 0.24);
    vec3 bandColor = mix(ionGas, cyanGas, bandDetail);
    col += bandColor * band * (0.14 + bandDetail * 0.36 + knots * 0.38) * (1.0 - transit * 0.52);

    float laneA = exp(-abs(signedBand + 0.055 + (bandDetail - 0.5) * 0.055) * 17.0);
    float laneB = exp(-abs(signedBand - 0.12 + (knots - 0.35) * 0.07) * 12.0);
    col *= 1.0 - (laneA * 0.46 + laneB * 0.26) * band;

    // Separate nebula banks make the sky feel three-dimensional rather than
    // like a single plane behind the Crown.
    float bankA = ss(0.43, 0.82, highCloud) * (0.35 + band * 0.65);
    float bankB = pow(ss(0.35, 0.88, lowCloud), 2.0);
    col += vec3(0.021, 0.082, 0.13) * bankA * 0.48;
    col += vec3(0.085, 0.020, 0.13) * bankB * (0.14 + 0.16 * (1.0 - band));

    // The destination sky carries the only persistent rose in the journey.
    float roseBank = pow(ss(0.46, 0.88, fbm2(rot(0.55) * q * 1.2 + 33.7)), 2.0);
    col += vec3(0.14, 0.018, 0.065) * roseBank * shore * 0.42;

    col += renderStars(q, transit, shore);
    return col;
  }

  float crownApproach(float t) {
    return ss(0.105, 0.405, t);
  }

  vec2 crownCenter(float t) {
    float portrait = 1.0 - ss(0.78, 1.08, uAspect);
    float approach = crownApproach(t);
    float inside = ss(0.545, 0.695, t);
    vec2 farCenter = mix(vec2(0.66, 0.16), vec2(0.10, 0.14), portrait);
    vec2 nearCenter = mix(vec2(-0.12, 0.035), vec2(0.0, 0.055), portrait);
    vec2 insideCenter = mix(vec2(-0.28, 0.20), vec2(-0.01, 0.16), portrait);
    return mix(mix(farCenter, nearCenter, approach), insideCenter, inside);
  }

  float crownRadius(float t) {
    float portrait = 1.0 - ss(0.78, 1.08, uAspect);
    float approach = crownApproach(t);
    float inside = ss(0.545, 0.695, t);
    float farRadius = mix(0.34, 0.27, portrait);
    float nearRadius = mix(0.96, 0.71, portrait);
    float insideRadius = mix(1.22, 0.92, portrait);
    return mix(mix(farRadius, nearRadius, approach), insideRadius, inside);
  }

  float crownWidth(float t) {
    float approach = crownApproach(t);
    float inside = ss(0.54, 0.69, t);
    return mix(mix(0.080, 0.192, approach), 0.315, inside);
  }

  void crownCoordinates(vec2 p, float t, out vec2 q, out float angle, out float radial, out float arc) {
    float portrait = 1.0 - ss(0.78, 1.08, uAspect);
    q = rot(-0.17 + t * 0.035) * (p - crownCenter(t));
    q.x *= mix(0.84, 1.22, portrait);
    q.y *= mix(1.04, 0.96, portrait);
    angle = atan(q.y, q.x);
    // Angular harmonics must stay integer so the ring closes on itself with
    // no seam at the atan wrap.
    float shape = 1.0
      + sin(angle * 2.0 + 0.62) * 0.075
      + sin(angle * 4.0 - 0.35) * 0.026;
    radial = length(q) - crownRadius(t) * shape;

    float gapCenter = mix(2.44, 2.80, portrait);
    float gapDistance = wrappedDistance(angle, gapCenter);
    float primaryGap = ss(0.24, 0.72, gapDistance);
    float broken = 0.42 + 0.58 * ss(-0.30, 0.45, sin(angle * 3.0 + 1.1));
    arc = primaryGap * mix(broken, 1.0, ss(0.24, 0.46, t));
    arc *= mix(0.62, 1.0, ss(-0.95, 0.15, sin(angle * 1.0 - 0.25)));
  }

  void sampleCrown(vec2 p, float z, float t, out float density, out vec3 emission) {
    vec2 q;
    float angle;
    float radial;
    float arc;
    crownCoordinates(p * (1.0 + z * 0.018), t, q, angle, radial, arc);

    float width = crownWidth(t);
    vec2 crossSection = vec2(radial, z * width * 0.92);
    float cloudDistance = length(crossSection) / width;
    // Noise is sampled on a circle in the ring's angular direction so the
    // pattern is periodic across the atan wrap instead of seaming.
    vec2 ring = vec2(cos(angle), sin(angle));
    float drift = uTime * uMotion * 0.006;
    float coarse = noise2(ring * 4.7 + vec2(z * 2.1 + uSeed * 0.31 + drift, radial * 13.0 - z * 1.9 + drift));
    float fine = noise2(ring * 10.0 + vec2(z * 4.5 + 17.0, z * 5.3 - radial * 27.0));
    float cloud = exp(-cloudDistance * cloudDistance * 2.15) * arc;
    cloud *= 0.32 + coarse * 0.78 + fine * 0.18;

    density = cloud * 0.64;
    emission = mix(vec3(0.035, 0.10, 0.34), vec3(0.035, 0.48, 0.61), coarse);
    emission *= cloud * 1.72;
    emission += vec3(0.18, 0.038, 0.38) * cloud * fine * 0.62;

    float separation = width * mix(0.31, 0.42, ss(0.18, 0.58, t));
    float filamentWidth = width * mix(0.145, 0.108, fine);
    for (int k = 0; k < 3; k++) {
      float fk = float(k);
      float phase = angle * 3.0 + fk * 2.0943951
        + sin(angle * 1.0 + 0.7) * 0.58
        + uTime * uMotion * 0.012;
      vec2 filamentCenter = separation * vec2(cos(phase), sin(phase));
      float d = length(crossSection - filamentCenter);
      float filament = exp(-d * d / max(filamentWidth * filamentWidth, 0.00001)) * arc;
      filament *= 0.72 + coarse * 0.48;
      float coreWidth = filamentWidth * 0.36;
      float core = exp(-d * d / max(coreWidth * coreWidth, 0.000001)) * arc;

      vec3 strand = fk < 0.5
        ? vec3(0.10, 0.82, 1.10)
        : (fk < 1.5 ? vec3(0.48, 0.18, 0.96) : vec3(0.82, 0.94, 1.02));
      emission += strand * filament * 1.82;
      emission += vec3(1.28, 1.32, 1.22) * core * 2.65;
      density += filament * 0.72 + core * 0.24;
    }

    // Front samples are sharper while the far side dissolves into gas. This
    // modest anisotropy supplies depth even in a still deterministic frame.
    float depthLight = mix(0.46, 1.08, ss(-1.0, 0.85, z));
    emission *= depthLight;
  }

  vec3 renderCrownVolume(vec2 p, float t, float presence) {
    vec3 sum = vec3(0.0);
    float transmittance = 1.0;
    float jitter = (hash21(gl_FragCoord.xy + uSeed * 37.0) - 0.5) * 0.34;
    float stepLength = 2.0 / max(uQuality, 1.0);

    for (int i = 0; i < MAX_CROWN_STEPS; i++) {
      if (float(i) >= uQuality) break;
      float z = -1.0 + (float(i) + 0.5 + jitter) * stepLength;
      float density;
      vec3 emission;
      sampleCrown(p, z, t, density, emission);
      sum += emission * transmittance * stepLength * presence * 1.52;
      transmittance *= exp(-density * stepLength * presence * 0.92);
      if (transmittance < 0.018) break;
    }
    return sum;
  }

  vec3 crownKnots(vec2 p, float t, float presence) {
    vec2 q;
    float angle;
    float radial;
    float arc;
    crownCoordinates(p, t, q, angle, radial, arc);
    float width = crownWidth(t);
    float band = exp(-pow(radial / max(width, 0.001), 2.0) * 1.8) * arc;
    float along = (angle + PI) / TAU;

    vec2 lattice = vec2(along * 104.0, radial / width * 8.0);
    vec2 id = floor(lattice);
    vec2 gv = fract(lattice) - 0.5;
    vec2 offset = (hash22(id + uSeed * 11.0) - 0.5) * 0.55;
    float alive = ss(0.72, 1.0, hash21(id + uSeed * 23.0));
    float size = mix(0.075, 0.255, pow(hash21(id + 8.1), 4.0));
    vec2 d = gv - offset;
    float point = ss(size, 0.0, length(d)) * alive;
    float spike = (exp(-abs(d.x) * 42.0) + exp(-abs(d.y) * 42.0))
      * ss(0.24, 0.0, length(d)) * alive * 0.28;

    vec2 microLattice = vec2(along * 238.0, radial / width * 14.0);
    vec2 mid = floor(microLattice);
    vec2 mgv = fract(microLattice) - 0.5;
    vec2 mo = (hash22(mid + uSeed * 41.0) - 0.5) * 0.68;
    float micro = ss(0.105, 0.0, length(mgv - mo));
    micro *= ss(0.90, 1.0, hash21(mid + uSeed * 61.0));

    vec3 color = mix(vec3(0.38, 0.82, 1.15), vec3(1.20, 1.13, 0.98), hash21(id + 4.7));
    float wispNoise = fbm2(vec2(cos(angle), sin(angle)) * 2.35 + vec2(uSeed * 0.2, radial / width * 2.8 + 7.0));
    float wisps = band * (0.18 + wispNoise * 0.82) * ss(0.34, 0.74, wispNoise);
    vec3 gas = mix(vec3(0.025, 0.21, 0.33), vec3(0.20, 0.035, 0.34), wispNoise);
    return (color * (point * 4.5 + spike * 2.0 + micro * 2.75) * band
      + gas * wisps * 1.45) * presence;
  }

  vec2 flareCenter(float t) {
    float portrait = 1.0 - ss(0.78, 1.08, uAspect);
    float approach = crownApproach(t);
    vec2 distant = mix(vec2(0.79, 0.02), vec2(0.18, -0.24), portrait);
    vec2 near = mix(vec2(0.74, -0.13), vec2(0.10, -0.39), portrait);
    return mix(distant, near, approach);
  }

  vec3 renderFlare(vec2 p, float t, float presence) {
    vec2 center = flareCenter(t);
    vec2 d = p - center;
    float r = length(d);
    float authored = presence * (1.0 - ss(0.585, 0.69, t));
    float core = exp(-r * r * 860.0);
    float bloom = exp(-r * r * 31.0);
    float halo = exp(-r * 5.2) / (1.0 + r * 7.0);
    float horizontal = exp(-abs(d.y) * 125.0) * exp(-abs(d.x) * 3.8);
    float diagonal = exp(-abs(d.y + d.x * 0.21) * 96.0) * exp(-r * 3.0);
    vec3 pearl = vec3(1.36, 1.39, 1.28);
    vec3 cyan = vec3(0.16, 0.84, 1.04);
    return (pearl * core * 3.4 + cyan * bloom * 0.48 + cyan * halo * 0.10
      + pearl * (horizontal + diagonal) * 0.105) * authored;
  }

  vec3 renderWaypointMoon(vec3 background, vec2 p, float t) {
    float visible = ss(0.405, 0.455, t) * (1.0 - ss(0.565, 0.615, t));
    if (visible <= 0.001) return background;

    float portrait = 1.0 - ss(0.78, 1.08, uAspect);
    float phase = saturate((t - 0.42) / 0.16);
    vec2 flare = flareCenter(t);
    float crossing = (0.5 - phase) * mix(0.58, 0.34, portrait);
    vec2 center = flare + vec2(crossing, sin(phase * PI) * 0.025);
    float radius = mix(0.188, 0.168, portrait);
    vec2 local = (p - center) / radius;
    float d = length(local);

    float outerRim = exp(-abs(d - 1.018) * 96.0) * visible;
    float daySide = ss(-0.22, 0.52, dot(normalize(local + vec2(0.001)), normalize(flare - center)));
    background += vec3(0.09, 0.64, 0.79) * outerRim * (0.18 + daySide * 0.9);

    float mask = ss(1.012, 0.992, d) * visible;
    if (mask > 0.001) {
      float z = sqrt(max(0.0, 1.0 - dot(local, local)));
      vec3 normal = normalize(vec3(local, z));
      vec2 towardFlare = normalize(flare - center + vec2(0.0001));
      vec3 lightDir = normalize(vec3(towardFlare, 0.34));
      float ndl = saturate(dot(normal, lightDir));
      float light = pow(ndl, 1.35);

      float terrain = fbm2(local * 4.6 + vec2(uSeed * 0.37, uSeed * 0.11));
      float highlands = ss(0.44, 0.79, terrain);
      float fine = noise2(local * 18.0 + uSeed * 2.0);
      float craterA = exp(-pow(length(local - vec2(-0.24, 0.18)) - 0.105, 2.0) * 950.0);
      float craterB = exp(-pow(length(local - vec2(0.31, -0.27)) - 0.065, 2.0) * 1650.0);
      float craters = (craterA + craterB) * (0.38 + z * 0.62);

      vec3 rock = mix(vec3(0.010, 0.014, 0.023), vec3(0.105, 0.125, 0.145), highlands);
      rock *= 0.72 + fine * 0.38;
      rock *= 0.09 + light * 0.91;
      rock -= craters * vec3(0.025, 0.030, 0.034);

      float fresnel = pow(1.0 - z, 3.2);
      float litRim = fresnel * pow(ndl, 0.65);
      rock += vec3(0.08, 0.47, 0.56) * litRim * 0.72;
      rock += vec3(0.31, 0.38, 0.41) * pow(ndl, 4.0) * highlands * 0.13;
      background = mix(background, max(rock, 0.0), mask);
    }
    return background;
  }

  vec3 renderCloseCurrent(vec2 p, float t) {
    float closeMix = ss(0.545, 0.605, t) * (1.0 - ss(0.695, 0.755, t));
    if (closeMix <= 0.001) return vec3(0.0);

    float portrait = 1.0 - ss(0.78, 1.08, uAspect);
    vec2 origin = mix(vec2(-0.05, -0.30), vec2(0.0, -0.27), portrait);
    vec2 q = rot(mix(-0.29, 0.06, portrait)) * (p - origin);
    float bend = sin(q.x * 1.18 + 0.5) * 0.12 + sin(q.x * 2.7 - 0.8) * 0.035;
    float spine = q.y - bend;
    float flowNoise = fbm2(vec2(q.x * 1.65 - uTime * uMotion * 0.012, spine * 5.8) + uSeed * 0.18);
    float cloud = exp(-abs(spine) * mix(4.2, 5.6, portrait));
    cloud *= 0.28 + flowNoise * 0.92;

    vec3 col = mix(vec3(0.025, 0.20, 0.34), vec3(0.18, 0.045, 0.36), flowNoise);
    col *= cloud * 0.78;

    for (int k = 0; k < 3; k++) {
      float fk = float(k);
      float weave = sin(q.x * 3.15 + fk * 2.094 + 0.45) * 0.088;
      float distanceToFilament = abs(spine - weave);
      float filament = exp(-distanceToFilament * distanceToFilament * 510.0);
      float core = exp(-distanceToFilament * distanceToFilament * 2350.0);
      vec3 strand = fk < 0.5
        ? vec3(0.08, 0.70, 0.94)
        : (fk < 1.5 ? vec3(0.45, 0.14, 0.82) : vec3(0.82, 0.90, 0.92));
      col += strand * filament * (0.34 + flowNoise * 0.28);
      col += vec3(1.12, 1.20, 1.15) * core * 0.56;
    }

    vec2 cell = vec2(q.x * 33.0, spine * 18.0);
    vec2 id = floor(cell);
    vec2 gv = fract(cell) - 0.5;
    vec2 offset = (hash22(id + uSeed * 101.0) - 0.5) * 0.62;
    float life = ss(0.82, 1.0, hash21(id + uSeed * 109.0));
    float knot = ss(0.19, 0.0, length(gv - offset)) * life * cloud;
    col += mix(vec3(0.44, 0.80, 1.05), vec3(1.22, 1.13, 0.94), hash21(id + 3.0)) * knot * 3.2;
    return col * closeMix;
  }

  vec3 renderPaleCurrent(vec2 p, float t, float transit) {
    if (transit <= 0.001) return vec3(0.0);
    float portrait = 1.0 - ss(0.78, 1.08, uAspect);
    vec2 center = mix(vec2(-0.16, 0.035), vec2(0.0, -0.06), portrait) + uLook * 0.04;
    vec2 q = p - center;
    q.x *= mix(1.0, 1.34, portrait);
    float r = length(q);
    float angle = atan(q.y, q.x);
    float localT = saturate((t - 0.70) / 0.16);
    float apertureRadius = mix(1.22, 0.28, localT);

    float angularCell = (angle + PI) / TAU * 196.0;
    float laneId = floor(angularCell);
    float laneUv = fract(angularCell) - 0.5;
    float laneLife = pow(hash11(laneId + uSeed * 47.0), 5.5);
    float thinLane = exp(-abs(laneUv) * mix(24.0, 72.0, laneLife));
    float dash = pow(ss(0.1, 1.0, sin(r * 45.0 - uTime * uMotion * 4.5 + laneLife * 9.0)), 4.0);
    float ray = thinLane * laneLife * (0.24 + dash * 0.76) * ss(0.08, 0.36, r);
    ray /= 0.34 + r * 0.58;

    float ring = exp(-abs(r - apertureRadius) * mix(7.0, 13.0, localT));
    float braid = 0.48 + 0.52 * sin(angle * 3.0 + r * 4.5 - t * 14.0);
    braid = 0.35 + pow(saturate(braid), 2.0) * 0.95;
    float innerFog = exp(-r * 1.25) * (0.35 + 0.65 * fbm2(q * 2.2 + uSeed));
    float sideCurrent = exp(-abs(q.y + sin(q.x * 1.3) * 0.13) * 4.2) * ss(0.25, 1.4, abs(q.x));

    vec3 pale = vec3(0.68, 1.06, 1.12);
    vec3 cyan = vec3(0.07, 0.54, 0.76);
    vec3 violet = vec3(0.34, 0.16, 0.78);
    vec3 col = mix(cyan, pale, laneLife) * ray * 2.5;
    col += mix(violet, pale, braid) * ring * braid * 0.72;
    col += cyan * innerFog * 0.31;
    col += mix(violet, cyan, localT) * sideCurrent * 0.28;
    return col * transit;
  }

  float galaxyPoint(vec2 p, float scale, float threshold, float armWeight, out float heat) {
    vec2 cell = p * scale;
    vec2 id = floor(cell);
    vec2 gv = fract(cell) - 0.5;
    vec2 offset = (hash22(id + uSeed * 71.0) - 0.5) * 0.72;
    float h = hash21(id + uSeed * 83.0);
    float alive = ss(threshold, 1.0, h) * ss(0.035, 0.24, armWeight + h * 0.18);
    float d = length(gv - offset);
    float size = mix(0.19, 0.42, pow(h, 5.0));
    heat = hash21(id + 17.3);
    return ss(size, 0.0, d) * alive;
  }

  vec4 renderFarShore(vec2 p, float t, float reveal) {
    if (reveal <= 0.001) return vec4(0.0);
    float portrait = 1.0 - ss(0.78, 1.08, uAspect);
    vec2 center = mix(vec2(0.08, -0.045), vec2(0.0, -0.03), portrait);
    vec2 g = p - center;
    g = rot(mix(-0.19, -0.74, portrait)) * g;
    g /= mix(0.93, 0.76, portrait);
    g.y *= mix(2.38, 1.62, portrait);

    float r = length(g);
    float angle = atan(g.y, g.x);
    float disc = exp(-r * 2.25) * (1.0 - ss(0.82, 1.43, r));
    float halo = exp(-r * 1.36) * (1.0 - ss(1.0, 1.72, r));
    float structure = fbm2(g * 5.4 + vec2(uSeed * 0.25, 9.3));
    float fine = fbm2(g * 13.0 - vec2(17.0, uSeed * 0.18));

    // Logarithmic arm phase, plus a weaker offset branch on every arm.
    float spiral = angle * 3.0 - log(r + 0.075) * 7.1 + uSeed * 0.09;
    float armDistance = abs(sin(spiral * 0.5));
    float arm = exp(-armDistance * armDistance * 42.0);
    float branchDistance = abs(sin((spiral + 0.72 + sin(r * 8.0) * 0.18) * 0.5));
    float branch = exp(-branchDistance * branchDistance * 76.0) * 0.56;
    float arms = max(arm, branch) * disc;
    arms *= 0.44 + structure * 0.76;

    float dustPhase = angle * 3.0 - log(r + 0.088) * 7.1 + 0.36 + uSeed * 0.09;
    float dustDistance = abs(sin(dustPhase * 0.5));
    float dust = exp(-dustDistance * dustDistance * 92.0) * disc;
    dust *= 0.38 + fine * 0.82;

    vec3 col = vec3(0.018, 0.035, 0.11) * halo * (0.55 + structure * 0.7);
    float armFog = ss(0.18, 0.86, arms + structure * 0.18) * disc;
    col += mix(vec3(0.055, 0.34, 0.62), vec3(0.34, 0.12, 0.62), structure) * arms * 1.34;
    col += mix(vec3(0.025, 0.16, 0.25), vec3(0.16, 0.035, 0.25), fine) * armFog * 0.38;
    col += vec3(0.42, 0.075, 0.23) * branch * disc * fine * 0.34;

    float h1;
    float h2;
    float starsA = galaxyPoint(g, 106.0, 0.885, arms + disc * 0.14, h1);
    float starsB = galaxyPoint(g, 224.0, 0.952, arms + disc * 0.10, h2);
    vec3 starA = mix(vec3(0.42, 0.73, 1.08), vec3(1.12, 1.02, 0.82), h1);
    vec3 starB = mix(vec3(0.58, 0.46, 1.08), vec3(0.88, 1.02, 1.08), h2);
    col += starA * starsA * disc * 3.15;
    col += starB * starsB * disc * 2.15;
    col *= 1.0 - dust * 0.72;

    float core = exp(-r * 13.5);
    float compact = exp(-r * r * 510.0);
    float coreLane = exp(-abs(g.y) * 26.0) * exp(-abs(g.x) * 3.7);
    col += vec3(1.28, 1.19, 0.98) * core * 2.15;
    col += vec3(1.42, 1.30, 1.10) * compact * 5.2;
    col += vec3(0.68, 0.50, 0.92) * coreLane * 0.12;

    float darken = saturate(dust * 0.82 + disc * 0.055);
    return vec4(col * reveal, darken * reveal);
  }

  void main() {
    vec2 p = (vUv - 0.5) * 2.0;
    p.x *= uAspect;
    p += uLook * vec2(0.18, 0.11);

    float t = uProgress;
    float transitIn = ss(0.695, 0.765, t);
    float transitOut = 1.0 - ss(0.845, 0.895, t);
    float transit = transitIn * transitOut;
    float shore = ss(0.855, 0.935, t);
    float crownReveal = ss(0.075, 0.19, t);
    float crownDepart = 1.0 - ss(0.685, 0.785, t);
    float crownPresence = crownReveal * crownDepart;

    vec3 col = renderDeepSky(p, transit, shore);

    // Last Light carries a quiet receding wake before the Crown is readable.
    float departure = 1.0 - ss(0.095, 0.175, t);
    vec2 wakeUv = rot(-0.46) * (p - vec2(-0.42, 0.22));
    float wake = exp(-abs(wakeUv.y) * 12.0) * ss(-1.25, 0.9, wakeUv.x);
    wake *= 0.25 + 0.75 * fbm2(vec2(wakeUv.x * 2.8, wakeUv.y * 9.0) + uSeed);
    col += vec3(0.075, 0.22, 0.30) * wake * departure * 0.32;

    col += renderCrownVolume(p, t, crownPresence);
    col += crownKnots(p, t, crownPresence);
    col += renderFlare(p, t, crownPresence);
    col = renderWaypointMoon(col, p, t);
    col += renderCloseCurrent(p, t);

    col *= mix(1.0, 0.72, transit);
    col += renderPaleCurrent(p, t, transit);

    vec4 galaxy = renderFarShore(p, t, shore);
    col *= 1.0 - galaxy.a;
    col += galaxy.rgb;

    // Act-level color separation remains understated, but ensures the seven
    // deterministic frames do not collapse into one cyan grade.
    float among = ss(0.55, 0.61, t) * (1.0 - ss(0.68, 0.74, t));
    col = mix(col, col * vec3(0.89, 0.96, 1.13) + vec3(0.012, 0.002, 0.028), among * 0.34);
    col = mix(col, col * vec3(0.93, 1.04, 1.06), transit * 0.34);
    col = mix(col, col * vec3(1.05, 0.91, 1.04) + vec3(0.007, 0.001, 0.006), shore * 0.28);

    // A restrained glass-level horizon reflection ties the exterior light to
    // the craft without painting a second interface over the image.
    float glassHaze = exp(-pow((p.y + 0.91) * 3.0, 2.0));
    col += vec3(0.025, 0.12, 0.14) * glassHaze * (0.035 + transit * 0.045);

    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;
